/**
 * Structured Telemetry & Diagnostics Logger for Nestly Appliance Rental.
 * Captures API performance, client runtime errors, and system events.
 */

export interface TelemetryEvent {
  id: string;
  timestamp: string;
  type: 'INFO' | 'WARNING' | 'ERROR' | 'METRIC';
  category: string;
  message: string;
  data?: Record<string, any>;
  durationMs?: number;
}

class TelemetryLogger {
  private events: TelemetryEvent[] = [];
  private maxLogs = 500;
  private listeners: ((event: TelemetryEvent) => void)[] = [];

  /**
   * Log an event with metadata and notify active telemetry subscribers.
   */
  log(
    type: TelemetryEvent['type'],
    category: string,
    message: string,
    data?: Record<string, any>,
    durationMs?: number
  ): TelemetryEvent {
    const event: TelemetryEvent = {
      id: `tel_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
      timestamp: new Date().toISOString(),
      type,
      category,
      message,
      data,
      durationMs,
    };

    this.events.unshift(event);
    if (this.events.length > this.maxLogs) {
      this.events.pop();
    }

    if (type === 'ERROR') {
      console.error(`[Telemetry ERROR] [${category}] ${message}`, data);
    } else if (type === 'WARNING') {
      console.warn(`[Telemetry WARN] [${category}] ${message}`, data);
    }

    this.listeners.forEach((listener) => listener(event));
    return event;
  }

  /**
   * Measure execution duration of an async operation.
   */
  async timeAsync<T>(
    category: string,
    operationName: string,
    fn: () => Promise<T>,
    data?: Record<string, any>
  ): Promise<T> {
    const start = performance.now();
    try {
      const result = await fn();
      const durationMs = Math.round(performance.now() - start);
      this.log('METRIC', category, `Async Op '${operationName}' succeeded`, { ...data }, durationMs);
      return result;
    } catch (error: any) {
      const durationMs = Math.round(performance.now() - start);
      this.log('ERROR', category, `Async Op '${operationName}' failed: ${error?.message || error}`, { ...data, error }, durationMs);
      throw error;
    }
  }

  /**
   * Subscribe to live telemetry events.
   */
  subscribe(listener: (event: TelemetryEvent) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  /**
   * Get all captured telemetry events.
   */
  getEvents(): TelemetryEvent[] {
    return [...this.events];
  }

  /**
   * Clear recorded telemetry logs.
   */
  clear(): void {
    this.events = [];
  }
}

export const telemetry = new TelemetryLogger();
